import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { formatDate, formatDateTime } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, User, Camera, FileText, Calendar, GitCompare, LayoutGrid, Trash } from 'lucide-react';
import { toast } from 'react-toastify';
import { usePatientProfile, usedeleteApi } from '../hooks/useApi';
import { deleteApi } from '@/api/Admin.api';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { hasAnyPermission } from '@/utils/permissions';
import { useAuth } from '@/context/AuthContext';

const PatientProfile = () => {
  const { currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  // State for image modal
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);



  const { data: patientData, isLoading: isLoadingPatient } = usePatientProfile(id);

  // Extract patient data from API response: {status, message, data: {...}}
  const patient = patientData?.data || {};

  // Extract arrays from patient data
  const photos = patient?.photos || [];
  const visits = patient?.visits || [];
  const beforeafter = patient?.before_after || [];
  const collages = patient?.collages || [];
  const consent = patient?.consent || null;

  const allTreatmentPlans = [
    patient?.treatment_plan && {

      plan: patient.treatment_plan,
      date: patient.created_at,
    },

    ...(patient?.photos || [])
      .filter(photo => photo.treatment_plan)
      .map(photo => ({

        plan: photo.treatment_plan,
        date: photo.captured_at,
      })),
  ].filter(Boolean);

  const cleanUrl = (url = '') => url.replace(/\\/g, '');

  const { mutate: deletePhoto, isPending: isDeleting } = usedeleteApi();

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };




  if (isLoadingPatient) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading patient data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate('/patients')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 sm:flex-initial">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{patient?.full_name || 'N/A'}</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Patient ID: {patient?.patient_uid || 'N/A'}</p>
          </div>
        </div>

        <div className="flex-1"></div>

        {hasAnyPermission(currentUser, ['capture.photo', 'all']) && (
          <Button
            onClick={() => navigate(`/capture?patientId=${patient?.id}`)}
            className="w-full sm:w-auto bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            disabled={!patient?.id}
          >
            <Camera className="w-4 h-4 mr-2" />
            Capture Photo
          </Button>
        )}
      </div>

      <Tabs defaultValue="overview" className="w-full ">
        <TabsList className="grid w-full h-fit realtive grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 sm:gap-2">
          <TabsTrigger value="overview" className="text-xs sm:text-sm px-1 sm:px-3">Overview</TabsTrigger>
          <TabsTrigger value="gallery" className="text-xs sm:text-sm px-1 sm:px-3">Gallery ({photos.length})</TabsTrigger>
          <TabsTrigger value="before-after" className="text-xs sm:text-sm px-1 sm:px-3">Before & After ({beforeafter.length})</TabsTrigger>
          <TabsTrigger value="progress" className="text-xs sm:text-sm px-1 sm:px-3">Collages ({collages.length})</TabsTrigger>
          <TabsTrigger value="consent" className="text-xs sm:text-sm px-1 sm:px-3">Consent</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 animate-fadeIn">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Patient Info Card */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary-600" />
                  Patient Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-medium text-gray-900">{patient.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Patient ID</p>
                    <p className="font-medium text-gray-900">{patient.patient_uid}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Age / Gender</p>
                    <p className="font-medium text-gray-900">{patient.age} / {patient.gender}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-medium text-gray-900">{patient.phone}</p>
                  </div>
                  <div className="">
                    <p className="text-sm text-gray-500">Address</p>
                    <Tippy content={patient.address} trigger="mouseenter click">
                      <p className="font-medium text-gray-900 cursor-pointer">
                        {truncateText(patient.address || 'Not provided', 30)}
                      </p>
                    </Tippy>

                  </div>
                  <div className="">
                    <p className="text-sm text-gray-500">Diagnosis</p>

                    {patient.diagnosis.map((diagnosis) => (
                      <Badge key={diagnosis} className="bg-primary-100 mr-1 text-primary-700 hover:bg-primary-100">
                        {diagnosis}
                      </Badge>
                    ))}

                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-primary-600" />
                    <span className="text-sm font-medium text-gray-700">Total Photos</span>
                  </div>
                  <span className="text-2xl font-bold text-primary-600">{photos.length}</span>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">Registered</span>
                  </div>
                  <p className="text-sm  text-gray-900">{patient?.created_at ? formatDate(patient.created_at) : 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Treatment Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Treatment Plans</CardTitle>
              </CardHeader>

              <CardContent>
                {allTreatmentPlans.length > 0 ? (
                  <div className="space-y-3 max-h-[200px] overflow-y-auto ">
                    {allTreatmentPlans.map((item, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 p-3 rounded-lg border flex justify-between items-center "
                      >

                        <p className="text-gray-700 w-60 sm:w-[25vw] whitespace-pre-line mt-1">
                          {item.plan}
                        </p>

                        <span className="text-xs text-gray-500 mt-1">
                          {formatDate(item.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No treatment plans recorded</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Medical History</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-line">
                  {patient?.medical_history || 'No medical history recorded'}
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Gallery Tab */}
        <TabsContent value="gallery" className="animate-fadeIn">
          {photos.length === 0 ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No photos yet</h3>
                  <p className="text-gray-600 mb-6">Start capturing patient photos</p>
                  {hasAnyPermission(currentUser, ['capture.photo', 'all']) && (
                    <Button
                      onClick={() => navigate(`/capture?patientId=${patient.id}`)}
                      className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Capture First Photo
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 animate-pop"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => {
                    setSelectedImage({
                      url: cleanUrl(photo?.original_url),
                      title: photo?.visit_type,

                    });
                    setIsImageModalOpen(true);
                  }}

                >
                  <img
                    src={cleanUrl(photo?.original_url)}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                    <button
                      className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-sm transition-all z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget({ id: photo.id, type: 'photo' });
                      }}
                    >
                      <Trash className="w-4 h-4 text-red-600" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-white text-sm font-medium truncate pr-2">{photo?.visit_type}</p>
                      <p className="text-white/80 text-xs">{formatDate(photo?.captured_at)}</p>

                      <Dialog open={deleteTarget?.id === photo.id && deleteTarget?.type === 'photo'} onOpenChange={() => setDeleteTarget(null)}>
                        <DialogContent
                          className="sm:max-w-md w-[95vw]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h2 className="text-lg font-semibold">Confirm Delete</h2>

                          <p className="text-sm text-gray-600 mt-2">
                            Are you sure you want to delete this photo? This action cannot be undone.
                          </p>

                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(null);
                              }}
                            >
                              Cancel
                            </Button>

                            <Button
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePhoto(deleteTarget);
                                setDeleteTarget(null);
                              }}
                            >
                              Delete
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>






                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Before & After Tab */}
        <TabsContent value="before-after" className="animate-fadeIn">
          {beforeafter.length === 0 ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <GitCompare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Before/After photos yet</h3>
                  <p className="text-gray-600 mb-6">Create before/after comparisons for this patient</p>
                  {hasAnyPermission(currentUser, ['before_after.create', 'all']) && (
                    <Button
                      onClick={() => navigate(`/before-after?patientId=${patient.id}`)}
                      className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                    >
                      <GitCompare className="w-4 h-4 mr-2" />
                      Create Before/After
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {beforeafter.map((collage) => {
                // TODO: Get photos from API when available
                // const allPhotos = await getPhotosApi();
                const allPhotos = [];


                return (
                  <Card key={collage.id} className="overflow-hidden hover:shadow-lg transition-all duration-300" onClick={(e) => e.stopPropagation()}>
                    <CardHeader className="pb-3">
                      <div className='flex justify-between'>
                        <div>
                          <CardTitle className="text-base font-semibold">{collage?.title || 'Before & After'}</CardTitle>
                          <p className="text-xs  text-gray-500 mt-1">{formatDate(collage?.created_at)}</p>
                        </div>

                        <Trash
                          className="w-6 h-6 text-red-600  bg-white rounded-lg p-1 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: collage.id, type: 'before_after' });
                          }}
                        />
                      </div>
                      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                        <DialogContent
                          className="sm:max-w-md"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h2 className="text-lg font-semibold">Confirm Delete</h2>
                          <p className="text-sm text-gray-600 mt-2">
                            Are you sure you want to delete this photo? This action cannot be undone.
                          </p>

                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(null);
                              }}
                            >
                              Cancel
                            </Button>

                            <Button
                              variant="destructive"
                              disabled={isDeleting}
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePhoto(deleteTarget, {
                                  onSuccess: () => {
                                    setDeleteTarget(null);
                                    toast.success("Deleted successfully");
                                  }
                                });
                                // setDeleteTarget(null);
                              }}
                            >
                              {isDeleting ? (
                                <>
                                  <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin mr-2"></span>
                                  Deleting...
                                </>
                              ) : (
                                'Delete'
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 gap-2">
                        <div
                          className="relative aspect-square rounded-lg  overflow-hidden border-2 border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => {
                            if (collage?.image_url) {
                              setSelectedImage({
                                url: cleanUrl(collage.image_url),
                                title: collage?.title || 'Before & After',
                              });
                              setIsImageModalOpen(true);
                            }
                          }}
                        >

                          {collage?.thumbnail_url ? (
                            <img
                              src={cleanUrl(collage?.thumbnail_url)}
                              alt="Before"
                              className="w-full  h-full object-cover pointer-events-none"

                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center pointer-events-none">
                              <Camera className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Progress Collage Tab */}
        <TabsContent value="progress" className="animate-fadeIn">
          {collages.length === 0 ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <LayoutGrid className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No collages yet</h3>
                  <p className="text-gray-600 mb-6">Create progress collages showing multiple photos for this patient</p>
                  {hasAnyPermission(currentUser, ['collage.create', 'all']) && (
                    <Button
                      onClick={() => navigate(`/collage?patientId=${patient.id}`)}
                      className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                    >
                      <LayoutGrid className="w-4 h-4 mr-2" />
                      Create Progress Collage
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {collages.map((collage) => (
                <Card key={collage.id} className="overflow-hidden hover:shadow-lg transition-all duration-300" >
                  <CardHeader className="pb-3 flex ">
                    <div className="flex flex-wrap justify-between" >
                      <div>
                        <div className="flex flex-wrap">
                          <CardTitle className="text-base   font-semibold">{collage?.title || 'Collage'}</CardTitle>
                        </div>
                        <div className="">
                          <p className="text-xs  text-gray-500">{formatDate(collage?.created_at)}</p>
                          {collage?.type && (
                            <Badge className="bg-primary-100 text-primary-700 hover:bg-primary-100">
                              {collage.type}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        <Trash
                          className="w-6 h-6 text-red-600  bg-white rounded-lg p-1 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: collage.id, type: 'collage' });
                          }}
                        />
                      </div>
                    </div>

                  </CardHeader>

                  <CardContent className="space-y-3">
                    {collage?.image_url ? (
                      <div
                        className="relative  aspect-square rounded-lg overflow-hidden border-2 border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (collage?.image_url) {
                            setSelectedImage({
                              url: cleanUrl(collage.image_url),
                              title: collage?.title || 'Collage'
                            });
                            setIsImageModalOpen(true);
                          }
                        }}
                      >
                        <img
                          src={cleanUrl(collage?.thumbnail_url)}
                          alt={collage?.title || 'Collage'}
                          className="w-full  h-full object-cover "

                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div style={{ display: 'none' }} className="w-full h-full bg-gray-100 flex items-center justify-center pointer-events-none">
                          <LayoutGrid className="w-12 h-12 text-gray-400" />
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                        <LayoutGrid className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                  </CardContent>
                  <Dialog open={deleteTarget?.id === collage.id && deleteTarget?.type === 'collage'} onOpenChange={() => setDeleteTarget(null)}>
                    <DialogContent
                      className="sm:max-w-md w-[95vw]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h2 className="text-lg font-semibold">Confirm Delete</h2>

                      <p className="text-sm text-gray-600 mt-2">
                        Are you sure you want to delete this photo? This action cannot be undone.
                      </p>

                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(null);
                          }}
                        >
                          Cancel
                        </Button>

                        <Button
                          variant="destructive"
                          disabled={isDeleting}
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePhoto(deleteTarget, {
                              onSuccess: () => {
                                setDeleteTarget(null);
                                toast.success("Collage deleted successfully");
                              }
                            });
                            // setDeleteTarget(null); 
                          }}
                        >
                          {isDeleting ? (
                            <>
                              <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin mr-2"></span>
                              Deleting...
                            </>
                          ) : (
                            'Delete'
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                </Card>

              ))}
            </div>
          )}
        </TabsContent>

        {/* Consent Tab */}
        {/* Consent Tab */}
        <TabsContent value="consent" className="animate-fadeIn">
          {consent ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-600" />
                    Digital Consent Form
                  </div>

                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <p className="text-sm text-gray-500">Signed On</p>
                    <p className="font-medium text-gray-900">
                      {consent?.signed_at ? formatDateTime(consent.signed_at) : 'N/A'}
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>

              {/* MAIN CONTENT */}
              <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* LEFT SIDE – CONSENT RESPONSES */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-4">
                    Consent Responses
                  </p>

                  {consent?.responses &&
                    Array.isArray(consent.responses) &&
                    consent.responses.length > 0 ? (
                    <div className="grid gap-4">
                      {consent.responses.map((response, index) => (
                        <div
                          key={response.key || index}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <p className="font-medium text-gray-900">
                            {response.consent_type_name || 'N/A'}
                          </p>

                          <Badge
                            className={
                              response.is_consented
                                ? 'bg-green-100 text-green-700 hover:bg-green-100'
                                : 'bg-red-100 text-red-700 hover:bg-red-100'
                            }
                          >
                            {response.is_consented ? 'Consented' : 'Not Consented'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No consent responses found</p>
                  )}
                </div>

                {/* RIGHT SIDE – SIGNED BY + SIGNATURE */}
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Signed By</p>
                    <p className="font-medium text-gray-900">
                      {patient?.full_name || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-2">Digital Signature</p>
                    <div className="border-2 w-full border-gray-200 rounded-lg p-4 bg-white">
                      <img
                        src={cleanUrl(patient?.consent?.signature_url)}
                        alt="Signature"
                        className=""
                      />
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No consent form found
                  </h3>
                  <p className="text-gray-600">
                    Consent form was not recorded for this patient
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

      </Tabs>

      {/* Image Modal */}
      <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
        <DialogContent className="max-w-6xl w-[95vw] h-[95vh] p-2 bg-transparent border-none shadow-none [&>button]:text-white [&>button]:hover:text-gray-300 [&>button]:z-50">
          {selectedImage && (
            <div className="relative  w-full flex items-center justify-center bg-black/80 rounded-lg overflow-hidden">
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full  max-h-[100vh] object-contain rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'w-full h-64 flex items-center justify-center text-white text-lg';
                  errorDiv.textContent = 'Failed to load image';
                  e.target.parentElement.appendChild(errorDiv);
                }}
              />
              {selectedImage.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent text-white p-4 rounded-b-lg">
                  <p className="text-lg font-semibold">{selectedImage.title}</p>
                </div>
              )}

            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientProfile;
